// ONE ARKit-52 frame per render frame, from whichever source owns the face.
//
// HeadModel has always resolved the face's owner inline while writing morph
// targets: a debug-forced pose, else the Rig Controller node, else the webcam Face
// Tracker node (ENG-1107), else LiveLink (the server's audio animation), else face
// clips, else idle — with the procedural
// blink and the eye-follow aim added on top of whichever won. The joint-driven
// face (faceRigDriver.js) needs the same answer as a VALUE rather than as morph
// writes, so a MetaHuman GLB with no blendshapes and myra's morph-driven head
// disagree about nothing: same source, same precedence, same overlays. This is
// that resolution, pure and allocation-free, so node:test pins the precedence.
import { ARKIT_NAMES } from './arkitNames.js'

export const ARKIT_LEN = 52
const BLINK_L = ARKIT_NAMES.indexOf('EyeBlinkLeft')
const BLINK_R = ARKIT_NAMES.indexOf('EyeBlinkRight')
if (BLINK_L < 0 || BLINK_R < 0) throw new Error('[faceFrame] ARKIT_NAMES has no EyeBlinkLeft/Right')

/**
 * @param {object} src
 * @param {ArrayLike<number>|null} [src.forcedArkit]  window.__FORCE_ARKIT__'s array; wins outright
 * @param {number} [src.rigWeight]        Rig Controller weight (rigControlWeight); >0 means the sliders own the face
 * @param {ArrayLike<number>|null} [src.rigArkit]  the composed rig-controller frame (composeRigControlArkit)
 * @param {number} [src.trackWeight]      webcam face-track weight (faceTrackWeight); >0 means the plugged Face Tracker node owns the face (ENG-1107)
 * @param {ArrayLike<number>|null} [src.trackArkit]  faceTrackState.arkit
 * @param {number} [src.liveWeight]       __livelink__ weight
 * @param {ArrayLike<number>|null} [src.liveArkit]  the buffer's current frame (getCurrentArkit)
 * @param {ArrayLike<number>|null} [src.clipWeights] face-clip blend (FaceClipPlayer.getBlendedWeights)
 * @param {number} [src.blink]            procedural blink envelope (currentBlinkWeight), 0 = none
 * @param {ArrayLike<number>|null} [src.eyeOverlay]  eyeFollowRuntime.arkitOverlay(): EyeLook channels, others 0
 * @param {Float32Array} out  52 floats, overwritten
 * @returns {'forced'|'rig'|'track'|'live'|'clips'|'idle'} which source owned the frame
 */
export function selectFaceArkitFrame(src, out) {
  let owner
  if (src.forcedArkit) {
    for (let i = 0; i < ARKIT_LEN; i++) out[i] = src.forcedArkit[i] || 0
    owner = 'forced'
  } else if (src.rigWeight > 0 && src.rigArkit) {
    const w = src.rigWeight
    for (let i = 0; i < ARKIT_LEN; i++) out[i] = (src.rigArkit[i] || 0) * w
    owner = 'rig'
  } else if (src.trackWeight > 0 && src.trackArkit) {
    const w = src.trackWeight
    for (let i = 0; i < ARKIT_LEN; i++) out[i] = (src.trackArkit[i] || 0) * w
    owner = 'track'
  } else if (src.liveWeight > 0 && src.liveArkit) {
    const w = src.liveWeight
    for (let i = 0; i < ARKIT_LEN; i++) out[i] = (src.liveArkit[i] || 0) * w
    owner = 'live'
  } else if (src.clipWeights) {
    for (let i = 0; i < ARKIT_LEN; i++) out[i] = src.clipWeights[i] || 0
    owner = 'clips'
  } else {
    out.fill(0)
    owner = 'idle'
  }
  // Overlays add on top of the owner, never replace it — the same rule the
  // morph path follows (applyBlink / applyEyeFollow run after the owner wrote).
  if (src.blink > 0) {
    if (src.blink > out[BLINK_L]) out[BLINK_L] = src.blink
    if (src.blink > out[BLINK_R]) out[BLINK_R] = src.blink
  }
  if (src.eyeOverlay) {
    const e = src.eyeOverlay
    for (let i = 0; i < ARKIT_LEN; i++) if (e[i] > out[i]) out[i] = e[i]
  }
  return owner
}
